import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import {
  Clover, LayoutDashboard, ArrowLeftRight, Tags, Wallet,
  FileText, Calculator, MessageCircle, Settings, LogOut,
  Menu, X, ChevronRight, Target, Repeat, Bell, Moon, Sun,
  TrendingUp, Brain, Crown, HelpCircle, Upload, CreditCard,
  PiggyBank, BellRing, Globe
} from 'lucide-react';
import { cn } from '../lib/utils';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, language, theme, toggleTheme, isCeo } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslation(language);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const baseNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/transactions', icon: ArrowLeftRight, label: t('transactions') },
    { path: '/recurring', icon: Repeat, label: 'Recurrentes' },
    { path: '/budgets', icon: Target, label: 'Presupuestos' },
    { path: '/savings-goals', icon: PiggyBank, label: 'Metas de Ahorro' },
    { path: '/investments', icon: TrendingUp, label: 'Inversiones' },
    { path: '/analysis', icon: Brain, label: 'Análisis IA' },
    { path: '/categories', icon: Tags, label: t('categories') },
    { path: '/accounts', icon: Wallet, label: t('accounts') },
    { path: '/import', icon: Upload, label: 'Importar Banco' },
    { path: '/currency', icon: Globe, label: 'Multi-Divisa' },
    { path: '/alerts', icon: BellRing, label: 'Alertas Inteligentes' },
    { path: '/reports', icon: FileText, label: t('reports') },
    { path: '/calculators', icon: Calculator, label: t('calculators') },
    { path: '/assistant', icon: MessageCircle, label: t('assistant') },
    { path: '/notifications', icon: Bell, label: 'Notificaciones' },
    { path: '/pricing', icon: CreditCard, label: 'Planes', highlight: user?.plan === 'free' },
    { path: '/settings', icon: Settings, label: t('settings') },
    { path: '/help', icon: HelpCircle, label: 'Ayuda' },
  ];

  // Add Admin panel for CEO
  const navItems = isCeo
    ? [...baseNavItems, { path: '/admin', icon: Crown, label: 'Panel CEO', highlight: true }]
    : baseNavItems;

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const isHighlight = item.highlight;

    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        className={cn(
          "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-all duration-200",
          isActive
            ? "bg-primary text-primary-foreground"
            : isHighlight
              ? "text-amber-500 hover:bg-amber-500/10 hover:text-amber-600"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          mobile ? "rounded-sm" : "rounded-sm"
        )}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <Icon size={18} strokeWidth={1.5} />
        <span>{item.label}</span>
        {isActive && <ChevronRight size={16} className="ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background" data-testid="main-layout">
      {/* Mobile header - perfectly synced with main padding */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-card border-b border-border z-50 flex items-center justify-between px-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-center gap-3 h-14">
          <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
            <Clover className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight text-foreground">TREBOL</span>
        </div>
        <div className="flex items-center gap-2 h-14">
          <button
            onClick={toggleTheme}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            data-testid="mobile-theme-toggle"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-muted-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - with safe area left for landscape */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transition-transform duration-300 sidebar-safe-left",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full safe-top">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between gap-3 px-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Clover className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-display font-bold text-lg tracking-tight text-foreground">TREBOL</span>
                <span className="text-xs text-muted-foreground block -mt-1">FINANCE</span>
              </div>
            </div>
            {/* Theme toggle in sidebar */}
            <button
              onClick={toggleTheme}
              className="hidden lg:flex p-2 text-muted-foreground hover:text-foreground transition-colors rounded-sm hover:bg-muted"
              data-testid="sidebar-theme-toggle"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
            {navItems.map(item => (
              <NavLink key={item.path} item={item} mobile />
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Link
              to="/help"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-4 py-3 w-full text-sm font-medium text-muted-foreground hover:text-emerald-700 hover:bg-emerald-50 rounded-sm transition-all duration-200 min-h-[44px] mb-1"
              data-testid="help-btn"
            >
              <HelpCircle size={18} strokeWidth={1.5} />
              <span>Ayuda</span>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10 min-h-[44px]"
              data-testid="logout-btn"
            >
              <LogOut size={18} strokeWidth={1.5} />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content - perfectly matches mobile header total height to prevent white gaps or overlap */}
      <main className="lg:ml-64 min-h-screen min-h-[100dvh] lg:pt-0" style={{ paddingTop: 'calc(3.5rem + max(1rem, env(safe-area-inset-top, 0px)))' }}>
        <div className="p-4 sm:p-6 lg:p-8 pb-safe" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
